import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth.store';
import LanguageSwitcher from './LanguageSwitcher';
import { isSupportedLocale } from '../i18n';
import { useNotificationsStore } from '../store/notifications.store';
import { useTenantContextStore } from '../store/tenant-context.store';
import { connectSocket, disconnectSocket } from '../services/socket';
import { authApi, publicApi, adminApi } from '../services/api';
import NotificationPanel from './NotificationPanel';
import { OfflineBanner } from './OfflineBanner';
import { InstallBanner } from './InstallBanner';
import PrivacyBanner from './PrivacyBanner';
import BottomNav from './BottomNav';
import CookieConsent from './CookieConsent';
import InactivityGuard from './InactivityGuard';
import { useBreakpoint } from '../hooks/useBreakpoint';
import {
  LayoutDashboard,
  Truck,
  Map,
  Bell,
  Megaphone,
  Settings,
  LogOut,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  History,
  FileText,
  Wrench,
  Fuel,
  Search,
  User,
  Users,
  Gauge,
  FolderOpen,
  Target,
  CreditCard,
  Building2,
  Shield,
  ClipboardList,
  PackageCheck,
  Palette,
  Settings2,
  Wallet,
  Monitor,
  HelpCircle,
  Radio,
  ShieldBan,
  BatteryCharging,
  MapPin,
  Route,
  Car,
} from 'lucide-react';
import clsx from 'clsx';

// Helper to adjust color brightness
function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + percent));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

interface LayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
  permission?: string;
  /** Minimum permission level required — 'view' (default), 'edit', or 'full'. */
  minLevel?: 'view' | 'edit' | 'full';
  /** Maximum permission level — item hidden for users at OR above this level.
   *  Use `hideAtLevel: 'edit'` to hide an entry from users with edit/full rights,
   *  e.g. a driver-only shortcut that would be redundant for dispatchers. */
  hideAtLevel?: 'view' | 'edit' | 'full';
  /** Optional add-on module key — if set, the item is only shown when the tenant has activated it */
  module?: string;
};

// `name` holds an i18n key (resolved via t() at render time).
const navigation: NavItem[] = [
  { name: 'layout.menu.dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'dashboard' },
  { name: 'layout.menu.map', href: '/map', icon: Map, permission: 'map' },
  { name: 'layout.menu.devices', href: '/devices', icon: Truck, permission: 'devices' },
  { name: 'layout.menu.groups', href: '/groups', icon: FolderOpen, permission: 'groups' },
  { name: 'layout.menu.drivers', href: '/drivers', icon: Users, permission: 'drivers' },
  { name: 'layout.menu.scoring', href: '/drivers/scores', icon: Gauge, permission: 'scores' },
  { name: 'layout.menu.missions', href: '/missions', icon: ClipboardList, permission: 'missions', minLevel: 'edit', module: 'FIELD_SERVICE' },
  { name: 'layout.menu.missions', href: '/my-missions', icon: ClipboardList, permission: 'missions', hideAtLevel: 'edit', module: 'FIELD_SERVICE' },
  { name: 'layout.menu.parcels', href: '/parcels', icon: PackageCheck, permission: 'parcels', minLevel: 'edit', module: 'POD' },
  { name: 'layout.menu.ev_vehicles', href: '/ev', icon: BatteryCharging, permission: 'ev', module: 'EV' },
  { name: 'layout.menu.public_chargers', href: '/ev/charging-map', icon: MapPin, permission: 'ev', module: 'EV' },
  { name: 'layout.menu.tours', href: '/tours', icon: Route, permission: 'vrp', minLevel: 'edit', module: 'VRP' },
  { name: 'layout.menu.tours', href: '/my-tours', icon: Route, permission: 'vrp', hideAtLevel: 'edit', module: 'VRP' },
  { name: 'layout.menu.geofences', href: '/geofences', icon: Target, permission: 'geofences' },
  { name: 'layout.menu.history', href: '/history', icon: History, permission: 'history' },
  { name: 'layout.menu.reports', href: '/reports', icon: FileText, permission: 'reports' },
  { name: 'layout.menu.maintenance', href: '/maintenance', icon: Wrench, permission: 'maintenance' },
  { name: 'layout.menu.fuel', href: '/fuel', icon: Fuel, permission: 'fuel', module: 'FUEL' },
  { name: 'layout.menu.beacons', href: '/beacons', icon: Radio, permission: 'beacons' },
  { name: 'layout.menu.alerts', href: '/alerts', icon: Bell, permission: 'alerts' },
  { name: 'layout.menu.settings', href: '/settings', icon: Settings, permission: 'settings' },
  { name: 'layout.menu.help', href: '/help', icon: HelpCircle },
];

const adminNavigation = [
  { name: 'layout.menu.admin_overview', href: '/admin', icon: Shield, permission: 'admin.dashboard' },
  { name: 'layout.menu.admin_tenants', href: '/admin/tenants', icon: Building2, permission: 'admin.tenants' },
  { name: 'layout.menu.admin_devices', href: '/admin/devices', icon: Truck, permission: 'admin.devices' },
  { name: 'layout.menu.admin_beacons', href: '/admin/beacons', icon: Radio, permission: 'admin.beacons' },
  { name: 'layout.menu.admin_subscriptions', href: '/admin/subscriptions', icon: CreditCard, permission: 'admin.subscriptions' },
  { name: 'layout.menu.admin_invoices', href: '/admin/invoices', icon: FileText, permission: 'admin.invoices' },
  { name: 'layout.menu.admin_tracker_params', href: '/admin/tracker-parameters', icon: Settings2, permission: 'admin.tracker_params' },
  { name: 'layout.menu.admin_ev_catalog', href: '/admin/ev-models', icon: BatteryCharging, permission: 'admin.ev_catalog' },
  { name: 'layout.menu.admin_notifications', href: '/admin/notifications', icon: Bell, permission: 'admin.notifications' },
  { name: 'layout.menu.admin_stripe', href: '/admin/stripe', icon: Wallet, permission: 'admin.stripe' },
  { name: 'layout.menu.admin_oem', href: '/admin/oem', icon: Car, permission: 'admin.settings' },
  { name: 'layout.menu.admin_appearance', href: '/admin/appearance', icon: Palette, permission: 'admin.appearance' },
  { name: 'layout.menu.admin_marketing', href: '/admin/marketing', icon: Megaphone, permission: 'admin.marketing' },
  { name: 'layout.menu.admin_audit_logs', href: '/admin/audit-logs', icon: ClipboardList, permission: 'admin.audit_logs' },
  { name: 'layout.menu.admin_ip_security', href: '/admin/ip-security', icon: ShieldBan, permission: 'admin.ip_security' },
  { name: 'layout.menu.admin_system', href: '/admin/system', icon: Monitor, permission: 'admin.system' },
  { name: 'layout.menu.admin_settings', href: '/admin/settings', icon: Settings, permission: 'admin.settings' },
];


interface SaasAppearance {
  companyName: string;
  companyTagline?: string;
  logo?: string;
  primaryColor: string;
}

// Storage key for sidebar state
const SIDEBAR_COLLAPSED_KEY = 'trackfleet_sidebar_collapsed';

// Clé localStorage pour l'état des accordéons
const NAV_SECTIONS_KEY = 'trackfleet_nav_sections';

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuthStore();
  const { selectedTenant, clearSelectedTenant } = useTenantContextStore();
  const { i18n, t } = useTranslation();

  // Sync i18next language with the persisted user preference. Runs whenever
  // the logged-in user's `language` field changes (e.g. on login, or after
  // updating it from another tab).
  useEffect(() => {
    if (user?.language && isSupportedLocale(user.language) && i18n.resolvedLanguage !== user.language) {
      void i18n.changeLanguage(user.language);
    }
  }, [user?.language, i18n]);

  const { isMobile, isTablet, isDesktop } = useBreakpoint();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Initialiser l'état replié/déplié de la sidebar depuis localStorage, par défaut replié
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved !== null ? saved === 'true' : true;
  });

  // Tablet: force collapsed sidebar always visible
  // Desktop: use user preference from localStorage
  const effectiveCollapsed = isTablet ? true : sidebarCollapsed;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const unreadCount = useNotificationsStore((state) => state.unreadCount);

  // Pending invoices count for admin badge
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);

  useEffect(() => {
    if (user?.role !== 'SUPER_ADMIN') return;
    const fetchPending = () => {
      adminApi.getPendingInvoices()
        .then((res) => setPendingInvoiceCount(res.data?.count || 0))
        .catch(() => {});
    };
    fetchPending();
    const interval = setInterval(fetchPending, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // État des sections accordéon (ouvert/fermé)
  const [navSections, setNavSections] = useState<{ tenant: boolean; admin: boolean }>(() => {
    try {
      const saved = localStorage.getItem(NAV_SECTIONS_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* ignorer */ }
    return { tenant: true, admin: true };
  });

  // Persister l'état des accordéons
  useEffect(() => {
    localStorage.setItem(NAV_SECTIONS_KEY, JSON.stringify(navSections));
  }, [navSections]);

  const toggleSection = (section: 'tenant' | 'admin') => {
    setNavSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // SaaS appearance settings
  const [saasAppearance, setSaasAppearance] = useState<SaasAppearance>({
    companyName: 'TrackFleet',
    companyTagline: 'GPS Tracking',
    primaryColor: '#1f8da1',
  });

  // Persist sidebar collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Fetch admin/tenant permissions and enabled modules for the authenticated user.
  // Runs on every mount: the auth store is persisted in localStorage, so a user
  // that logged in before a module was activated would otherwise keep a stale
  // enabledModules list forever. This effect refreshes it on each page load.
  useEffect(() => {
    if (!user) return;
    authApi
      .me()
      .then((res) => {
        const data = res.data as {
          adminRoleId?: string | null;
          adminPermissions?: string[];
          tenantPermissions?: string[];
          enabledModules?: string[];
          language?: string;
          cguAccepted?: boolean;
          mustSetup2FA?: boolean;
          twoFactorEnabled?: boolean;
        };
        setUser({
          ...user,
          ...(data.adminRoleId !== undefined && { adminRoleId: data.adminRoleId }),
          ...(data.adminPermissions && { adminPermissions: data.adminPermissions }),
          ...(data.tenantPermissions && { tenantPermissions: data.tenantPermissions }),
          ...(data.enabledModules && { enabledModules: data.enabledModules }),
          ...(data.language && { language: data.language }),
          // Onboarding flags: keep them fresh so ProtectedRoute enforces CGU/2FA even for
          // sessions opened before enforcement was enabled (persisted store would be stale).
          ...(typeof data.cguAccepted === 'boolean' && { cguAccepted: data.cguAccepted }),
          ...(typeof data.mustSetup2FA === 'boolean' && { mustSetup2FA: data.mustSetup2FA }),
          ...(typeof data.twoFactorEnabled === 'boolean' && { twoFactorEnabled: data.twoFactorEnabled }),
        });
      })
      .catch(() => {
        // Ignore — permissions stay as-is
      });
    // Intentionally only re-run when the userId changes — not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Connect WebSocket globally so notifications work on every page
  useEffect(() => {
    if (user) {
      try {
        connectSocket();
      } catch {
        // Socket connection failed — non-blocking
      }
    }
    return () => {
      disconnectSocket();
    };
  }, [user?.id]);

  // Fetch SaaS appearance settings and apply CSS variables
  useEffect(() => {
    publicApi.getAppearance().then((data) => {
      setSaasAppearance(data);
      // Apply primary color as CSS variable for global use
      if (data.primaryColor) {
        document.documentElement.style.setProperty('--color-primary', data.primaryColor);
        // Also set variations for hover states, etc.
        document.documentElement.style.setProperty('--color-primary-dark', adjustColor(data.primaryColor, -20));
        document.documentElement.style.setProperty('--color-primary-light', adjustColor(data.primaryColor, 20));
      }
    }).catch(() => {
      // Keep defaults on error
    });
  }, []);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors
    }
    disconnectSocket();
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Mobile sidebar backdrop (only on mobile when drawer is open) */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — hidden on mobile (bottom nav used instead), collapsed on tablet, full on desktop */}
      <aside
        className={clsx(
          'fixed left-0 bottom-0 z-50 glass-sidebar transform transition-all duration-300 ease-in-out overflow-visible flex flex-col',
          // Mobile: drawer overlay, starts below header (top-16)
          isMobile && (sidebarOpen ? 'translate-x-0 w-72 top-16' : '-translate-x-full w-72 top-16'),
          // Tablet: always visible, always collapsed (icon-only), full height
          isTablet && 'translate-x-0 w-20 top-0',
          // Desktop: always visible, width depends on collapsed state, full height
          isDesktop && (sidebarCollapsed ? 'translate-x-0 w-20 top-0' : 'translate-x-0 w-72 top-0')
        )}
      >
        {/* Logo */}
        <div className={clsx(
          "flex items-center h-20 border-b border-gray-200",
          effectiveCollapsed ? "lg:justify-center md:justify-center lg:px-2 md:px-2 px-6" : "justify-between px-6"
        )}>
          <Link to="/dashboard" className="flex items-center gap-3">
            {saasAppearance.logo ? (
              <img
                src={saasAppearance.logo}
                alt={saasAppearance.companyName}
                className="w-10 h-10 rounded-xl object-contain"
              />
            ) : (
              <img
                src="/pwa-512x512.png"
                alt={saasAppearance.companyName}
                className="w-10 h-10 rounded-xl"
              />
            )}
            <div className={clsx(effectiveCollapsed && !isMobile && "hidden")}>
              <span className="text-xl font-bold text-gray-900">{saasAppearance.companyName}</span>
              <span className="block text-xs text-gray-500">{saasAppearance.companyTagline || 'GPS Tracking'}</span>
            </div>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className={clsx(
              "p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors",
              isMobile ? "block" : "hidden"
            )}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Collapse toggle button (desktop only) */}
        {isDesktop && (
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-24 z-50 w-6 h-6 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-colors"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-gray-500" />
            )}
          </button>
        )}

        {/* Scrollable sidebar content */}
        <div className="flex-1 overflow-y-auto no-scrollbar">
        {/* Tenant Context Indicator */}
        {user?.role === 'SUPER_ADMIN' && selectedTenant && (
          <div className={clsx("mt-4", effectiveCollapsed && !isMobile ? "px-2" : "px-4")}>
            <div className={clsx(
              "bg-cyan-50 border border-cyan-200 rounded-lg",
              effectiveCollapsed && !isMobile ? "p-2" : "p-3"
            )}>
              <div className={clsx(
                "flex items-center",
                effectiveCollapsed && !isMobile ? "justify-center flex-col gap-2" : "justify-between"
              )}>
                <div className={clsx("flex items-center gap-2", effectiveCollapsed && !isMobile && "hidden")}>
                  <Building2 className="h-4 w-4 text-cyan-600" />
                  <div>
                    <p className="text-xs text-cyan-600 font-medium">Tenant</p>
                    <p className="text-sm font-semibold text-cyan-800 truncate max-w-[140px]">
                      {selectedTenant.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={clearSelectedTenant}
                  className={clsx(
                    "p-1.5 hover:bg-cyan-100 rounded-lg text-cyan-600 transition-colors",
                    effectiveCollapsed && !isMobile && "hidden"
                  )}
                  title="Quitter"
                >
                  <X className="h-4 w-4" />
                </button>
                {/* Collapsed state */}
                {effectiveCollapsed && !isMobile && (
                  <div className="flex flex-col items-center gap-1">
                    <Building2 className="h-5 w-5 text-cyan-600" />
                    <button
                      onClick={clearSelectedTenant}
                      className="p-1 hover:bg-cyan-100 rounded text-cyan-600"
                      title={`Quitter ${selectedTenant.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tenant (accordéon) */}
        <nav className={clsx("mt-6", effectiveCollapsed && !isMobile ? "px-2" : "px-4")}>
          {/* En-tête accordéon Tenant */}
          <button
            onClick={() => toggleSection('tenant')}
            className={clsx(
              "w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors group",
              effectiveCollapsed && !isMobile && "justify-center px-2"
            )}
          >
            <div className={clsx("flex items-center gap-2", effectiveCollapsed && !isMobile && "hidden")}>
              <Truck className="h-4 w-4 text-green-600" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Gestion flotte
              </span>
            </div>
            <ChevronDown className={clsx(
              'h-4 w-4 text-gray-400 transition-transform',
              !navSections.tenant && '-rotate-90',
              effectiveCollapsed && !isMobile && 'hidden'
            )} />
            {/* Icône repliée */}
            {effectiveCollapsed && !isMobile && (
              <div className="flex items-center gap-1">
                <Truck className="h-4 w-4 text-green-600" />
                <ChevronDown className={clsx(
                  'h-3 w-3 text-gray-400 transition-transform',
                  !navSections.tenant && '-rotate-90'
                )} />
              </div>
            )}
          </button>

          {/* Liens tenant */}
          <div className={clsx(
            "space-y-1 overflow-hidden transition-all duration-200",
            navSections.tenant ? "max-h-[1100px] opacity-100 mt-1" : "max-h-0 opacity-0"
          )}>
            {navigation
              .filter((item) => {
                // Add-on module gate — hidden until the tenant has activated the module
                // (SUPER_ADMIN always sees every add-on for admin purposes)
                if (item.module && user?.role !== 'SUPER_ADMIN') {
                  if (!user?.enabledModules?.includes(item.module)) return false;
                }
                // No permission key = always visible (e.g. Aide)
                if (!item.permission) return true;

                const levelRank: Record<'view' | 'edit' | 'full', number> = { view: 0, edit: 1, full: 2 };

                // Compute the user's effective level for this permission key
                let userRank: number;
                if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') {
                  // SUPER_ADMIN and tenant ADMIN always have full level on every permission
                  userRank = levelRank.full;
                } else {
                  const perms = user?.tenantPermissions;
                  if (!perms) {
                    // Permissions not yet loaded — show the item to avoid a flash
                    // of missing entries (the effect that hydrates permissions
                    // runs on mount and refreshes the sidebar shortly after)
                    return true;
                  } else {
                    let max = -1;
                    for (const p of perms) {
                      const [key, level] = p.split(':');
                      if (key !== item.permission) continue;
                      const r = levelRank[level as 'view' | 'edit' | 'full'];
                      if (r !== undefined && r > max) max = r;
                    }
                    if (max < 0) return false; // user has no grant for this permission at all
                    userRank = max;
                  }
                }

                // hideAtLevel: hide the item when the user is AT or ABOVE the threshold.
                // Use case: a driver-only shortcut hidden from dispatchers.
                if (item.hideAtLevel) {
                  if (userRank >= levelRank[item.hideAtLevel]) return false;
                }

                // minLevel: require the user to be AT or ABOVE the threshold (default: view)
                const requiredRank = levelRank[item.minLevel ?? 'view'];
                return userRank >= requiredRank;
              })
              .map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  title={effectiveCollapsed && !isMobile ? t(item.name) : undefined}
                  className={clsx(
                    'nav-link',
                    isActive && 'nav-link-active',
                    effectiveCollapsed && !isMobile && 'justify-center px-2'
                  )}
                >
                  <item.icon
                    className={clsx(
                      'h-5 w-5 transition-colors flex-shrink-0',
                      isActive ? 'text-primary-600' : 'text-gray-500'
                    )}
                  />
                  <span className={clsx(
                    'font-medium',
                    isActive ? 'text-primary-600' : 'text-gray-700',
                    effectiveCollapsed && !isMobile && 'hidden'
                  )}>
                    {t(item.name)}
                  </span>
                  {item.name === 'layout.menu.alerts' && !effectiveCollapsed && unreadCount > 0 && (
                    <span className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-600">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Navigation Admin (accordéon) */}
        {user?.role === 'SUPER_ADMIN' && (
          <nav className={clsx("mt-4", effectiveCollapsed && !isMobile ? "px-2" : "px-4")}>
            {/* En-tête accordéon Admin */}
            <button
              onClick={() => toggleSection('admin')}
              className={clsx(
                "w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors group",
                effectiveCollapsed && !isMobile && "justify-center px-2"
              )}
            >
              <div className={clsx("flex items-center gap-2", effectiveCollapsed && !isMobile && "hidden")}>
                <Shield className="h-4 w-4 text-purple-600" />
                <span className="text-xs font-semibold text-purple-500 uppercase tracking-wider">
                  Administration
                </span>
              </div>
              <ChevronDown className={clsx(
                'h-4 w-4 text-purple-400 transition-transform',
                !navSections.admin && '-rotate-90',
                effectiveCollapsed && !isMobile && 'hidden'
              )} />
              {/* Icône repliée */}
              {effectiveCollapsed && !isMobile && (
                <div className="flex items-center gap-1">
                  <Shield className="h-4 w-4 text-purple-600" />
                  <ChevronDown className={clsx(
                    'h-3 w-3 text-purple-400 transition-transform',
                    !navSections.admin && '-rotate-90'
                  )} />
                </div>
              )}
            </button>

            {/* Liens admin */}
            <div className={clsx(
              "space-y-1 overflow-hidden transition-all duration-200",
              navSections.admin ? "max-h-[1000px] opacity-100 mt-1" : "max-h-0 opacity-0"
            )}>
              {adminNavigation
                .filter((item) => {
                  // If user has no adminPermissions array (legacy or not fetched yet), show all
                  const perms = user?.adminPermissions;
                  if (!perms) return true;
                  // Support graduated permissions: "admin.X" or "admin.X:view/edit/full"
                  return perms.includes(item.permission) ||
                    perms.some((p: string) => p.startsWith(item.permission + ':'));
                })
                .map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    title={effectiveCollapsed && !isMobile ? t(item.name) : undefined}
                    className={clsx(
                      'nav-link',
                      isActive && 'nav-link-active',
                      effectiveCollapsed && !isMobile && 'justify-center px-2'
                    )}
                  >
                    <item.icon
                      className={clsx(
                        'h-5 w-5 transition-colors flex-shrink-0',
                        isActive ? 'text-primary-600' : 'text-gray-500'
                      )}
                    />
                    <span className={clsx(
                      'font-medium',
                      isActive ? 'text-primary-600' : 'text-gray-700',
                      effectiveCollapsed && !isMobile && 'hidden'
                    )}>
                      {t(item.name)}
                    </span>
                    {item.name === 'layout.menu.admin_invoices' && !effectiveCollapsed && pendingInvoiceCount > 0 && (
                      <span className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-600">
                        {pendingInvoiceCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}

        </div>
      </aside>

      {/* Main content */}
      <div className={clsx(
        "min-h-screen flex flex-col transition-all duration-300",
        // Mobile: no left padding (no sidebar visible)
        isMobile && "pl-0",
        // Tablet: always collapsed sidebar (80px)
        isTablet && "pl-20",
        // Desktop: depends on collapsed state
        isDesktop && (sidebarCollapsed ? "pl-20" : "pl-72")
      )}>
        {/* Tenant Context Banner */}
        {user?.role === 'SUPER_ADMIN' && selectedTenant && (
          <div className="sticky top-0 z-40 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white px-4 py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Mode administration tenant : <strong>{selectedTenant.name}</strong>
                </span>
              </div>
              <button
                onClick={clearSelectedTenant}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
                Quitter
              </button>
            </div>
          </div>
        )}

        {/* Top bar */}
        <header className="sticky top-0 z-30 glass-header pt-safe">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            {/* Mobile: no hamburger (BottomNav has "Plus" button instead) */}
            {/* Tablet/Desktop: no hamburger needed (sidebar always visible) */}
            <div className="md:hidden w-8" /> {/* Spacer on mobile to balance header */}

            {/* Search bar */}
            <div className="hidden md:flex flex-1 max-w-md mx-4">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un véhicule..."
                  className="w-full pl-10 pr-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/20 transition-all"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className={clsx(
                    'relative p-2 rounded-xl transition-colors',
                    notificationsOpen
                      ? 'text-green-600 bg-green-50'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  )}
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold bg-red-500 text-white rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Backdrop */}
                {notificationsOpen && (
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setNotificationsOpen(false)}
                  />
                )}

                <NotificationPanel
                  isOpen={notificationsOpen}
                  onClose={() => setNotificationsOpen(false)}
                />
              </div>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 p-1.5 pr-3 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{user?.role}</p>
                  </div>
                  <ChevronDown className={clsx(
                    'h-4 w-4 text-gray-500 transition-transform',
                    userMenuOpen && 'rotate-180'
                  )} />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 glass-card py-2 z-20">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {user?.email}
                        </p>
                      </div>

                      <div className="py-1">
                        {/* Mon profil — toujours visible (lien vers la page profile dans /settings) */}
                        <Link
                          to="/settings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                        >
                          <User className="h-4 w-4" />
                          {t('layout.user_menu.profile')}
                        </Link>
                        {/* Facturation — visible si l'utilisateur a la permission billing */}
                        {(() => {
                          if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') return true;
                          const perms = user?.tenantPermissions;
                          if (!perms) return false;
                          return perms.some((p) => p.startsWith('billing:'));
                        })() && (
                          <Link
                            to="/billing"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                          >
                            <CreditCard className="h-4 w-4" />
                            {t('layout.menu.billing')}
                          </Link>
                        )}
                        {/* Paramètres — visible si l'utilisateur a la permission settings */}
                        {(() => {
                          if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') return true;
                          const perms = user?.tenantPermissions;
                          if (!perms) return false;
                          return perms.some((p) => p.startsWith('settings:'));
                        })() && (
                          <Link
                            to="/settings"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                          >
                            <Settings className="h-4 w-4" />
                            {t('common.settings')}
                          </Link>
                        )}
                      </div>

                      {/* Language picker */}
                      <div className="border-t border-gray-100 py-1">
                        <LanguageSwitcher />
                      </div>

                      <div className="border-t border-gray-100 pt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="h-4 w-4" />
                          {t('common.logout')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Install Banner (PWA) */}
        <InstallBanner />

        {/* Offline Banner */}
        <OfflineBanner />

        {/* Privacy Banner — self-gating: only renders while one of the user's
            own vehicles is inside a privacy window. */}
        <PrivacyBanner />

        {/* Page content — extra bottom padding on mobile for BottomNav */}
        <main className={clsx("flex-1 p-4 lg:p-6", isMobile && "pb-20")}>
          {children}
        </main>
      </div>

      {/* Bottom navigation bar (mobile only) */}
      {isMobile && (
        <BottomNav onMenuClick={() => setSidebarOpen(true)} />
      )}

      {/* Cookie/Privacy consent banner */}
      <CookieConsent />

      {/* Inactivity warning + auto-logout */}
      <InactivityGuard />
    </div>
  );
}
